/**
 * Walk-in entry (G3-G5) — visitor / delivery / cab at the gate. Photo via
 * camera (baked module), company quick-picks, flat code → sends the resident
 * an approval push, then hands off to the waiting screen.
 */
import * as ImagePicker from 'expo-image-picker';
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Image, Platform, Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { CompanyChips } from '@/components/companies/CompanyPicker';
import { Segmented, Toggle } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, Field, Input, Screen, TopBar } from '@/ui';

const TYPES = ['Visitor', 'Delivery', 'Cab'];

type FlatDir = { wings: { code: string; name: string }[]; flats: { code: string; wing: string }[] };

/** Wing → flat picker with type-ahead (user ask 2026-07-19): guards tap a
 * wing then the flat, or just type — matching codes surface as chips. Falls
 * back to a plain text field if the directory fetch fails. */
function FlatPicker({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  const { t } = useT();
  const [dir, setDir] = React.useState<FlatDir | null>(null);
  const [wing, setWing] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    void api.get<FlatDir>('/api/gate/flats')
      .then((d) => { if (!cancelled && Array.isArray(d.flats)) setDir(d); })
      .catch(() => { /* picker degrades to the plain input */ });
    return () => { cancelled = true; };
  }, []);

  const q = value.trim().toUpperCase();
  const exact = dir?.flats.some((f) => f.code === q) ?? false;
  const suggestions = React.useMemo(() => {
    if (!dir) return [];
    if (q && !exact) return dir.flats.filter((f) => f.code.startsWith(q)).slice(0, 24);
    if (!q && wing) return dir.flats.filter((f) => f.wing === wing);
    return [];
  }, [dir, q, exact, wing]);

  return (
    <View style={{ gap: space.s2 }}>
      <Input value={value} onChangeText={onChange} placeholder={dir ? t('type_or_pick') : 'A601'} autoCapitalize="characters" />
      {dir && dir.wings.length > 1 ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {dir.wings.map((w) => (
            <Chip key={w.code} label={t('wing_chip', { code: w.code })} active={wing === w.code && !q}
              onPress={() => { setWing(wing === w.code ? null : w.code); onChange(''); }} />
          ))}
        </View>
      ) : null}
      {suggestions.length > 0 ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {suggestions.map((f) => (
            <Chip key={f.code} label={f.code} active={f.code === q} onPress={() => onChange(f.code)} />
          ))}
        </View>
      ) : null}
    </View>
  );
}
const TYPE_CODE: Record<string, string> = { Visitor: 'VISITOR', Delivery: 'DELIVERY', Cab: 'CAB' };
const CODE_TYPE: Record<string, string> = { VISITOR: 'Visitor', DELIVERY: 'Delivery', CAB: 'Cab' };

export default function GuardEntry() {
  const { colors } = useTheme();
  const { t } = useT();
  const params = useLocalSearchParams<{ type?: string }>();
  const [kind, setKind] = React.useState(CODE_TYPE[params.type ?? ''] ?? 'Visitor');
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [flatCode, setFlatCode] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [vehicleNo, setVehicleNo] = React.useState('');
  const [companions, setCompanions] = React.useState('');
  const [leaveAtGate, setLeaveAtGate] = React.useState(false);
  const [photo, setPhoto] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const capture = async () => {
    try {
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      const result = perm.granted
        ? await ImagePicker.launchCameraAsync({ base64: true, quality: 0.4, exif: false })
        : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], base64: true, quality: 0.4, exif: false });
      if (!result.canceled && result.assets?.[0]?.base64) {
        const a = result.assets[0];
        const mime = a.mimeType?.startsWith('image/') ? a.mimeType : 'image/jpeg';
        setPhoto(`data:${mime};base64,${a.base64}`);
      }
    } catch {
      setError(t('err_camera'));
    }
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = await api.post<{ entry: { id: number; status: string } }>('/api/gate/entries', {
        type: TYPE_CODE[kind],
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        flatCode: flatCode.trim(),
        company: company || undefined,
        vehicleNo: kind === 'Cab' ? vehicleNo.trim() || undefined : undefined,
        companions: Number(companions) || 0,
        leaveAtGate: kind === 'Delivery' ? leaveAtGate : false,
        photo,
      });
      if (body.entry.status === 'LEFT_AT_GATE') {
        router.back();
      } else {
        router.replace(`/guard/waiting?id=${body.entry.id}` as '/guard');
      }
    } catch (e) {
      const status = (e as { status?: number }).status;
      setError(status === 404 ? t('err_flat_not_found')
        : status === 400 ? t('err_fill_name')
        : t('err_entry_generic'));
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = flatCode.trim().length > 0 && (name.trim().length > 0 || (kind === 'Delivery' && !!company));

  return (
    <Screen scroll={false}>
      <TopBar title={t('new_entry')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <Field label={t('who_at_gate')}>
          <Segmented options={TYPES} labels={[t('type_visitor'), t('type_delivery'), t('type_cab')]} value={kind} onChange={(v) => { setKind(v); setCompany(''); }} />
        </Field>

        {kind !== 'Visitor' ? (
          <Field label={kind === 'Delivery' ? t('company') : t('cab_service')}>
            <CompanyChips kind={kind === 'Delivery' ? 'delivery' : 'cab'} value={company} onChange={setCompany} />
          </Field>
        ) : null}

        <Field label={kind === 'Delivery' ? t('person_name_opt') : t('name_lbl')}>
          <Input value={name} onChangeText={setName} placeholder={t('full_name')} />
        </Field>

        <Field label={t('flat_lbl')}>
          <FlatPicker value={flatCode} onChange={setFlatCode} />
        </Field>

        <Field label={t('phone_opt')}>
          <Input value={phone} onChangeText={setPhone} placeholder="98XXXXXX21" keyboardType="phone-pad" />
        </Field>

        {kind === 'Cab' ? (
          <Field label={t('vehicle_number')}>
            <Input value={vehicleNo} onChangeText={(t) => setVehicleNo(t.toUpperCase())} placeholder="MH 01 AB 1234" autoCapitalize="characters" />
          </Field>
        ) : null}

        <View style={{ flexDirection: 'row', gap: space.s3, alignItems: 'flex-end' }}>
          <View>
            <AppText variant="label" tone="tertiary" style={{ marginBottom: 6 }}>{t('visitor_photo')}</AppText>
            {photo ? (
              <Pressable accessibilityRole="button" accessibilityLabel="Retake photo" onPress={() => void capture()}>
                <Image source={{ uri: photo }} style={{ width: 84, height: 84, borderRadius: 12, backgroundColor: colors.canvas2 }} />
              </Pressable>
            ) : (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Capture photo"
                onPress={() => void capture()}
                style={{
                  width: 84, height: 84, borderRadius: 12, borderWidth: 1.5, borderStyle: 'dashed',
                  borderColor: colors.borderFirm, backgroundColor: colors.canvas1,
                  alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <Icon name="plus" size={18} color={colors.textTertiary} />
                <AppText variant="bodySm" weight="600" tone="secondary">{Platform.OS === 'web' ? t('add_photo') : t('take_photo')}</AppText>
              </Pressable>
            )}
          </View>
          <View style={{ flex: 1 }}>
            <Field label={t('companions')}>
              <Input value={companions} onChangeText={setCompanions} placeholder="0" keyboardType="numeric" />
            </Field>
          </View>
        </View>

        {kind === 'Delivery' ? (
          <View style={{
            backgroundColor: colors.canvas1, borderRadius: 12, padding: space.s4,
            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <View style={{ flex: 1, paddingRight: space.s3 }}>
              <AppText variant="bodyMd" weight="500">{t('leave_at_gate_lbl')}</AppText>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                {t('leave_at_gate_sub')}
              </AppText>
            </View>
            <Toggle on={leaveAtGate} onChange={setLeaveAtGate} label={t('leave_at_gate')} />
          </View>
        ) : null}

        {error ? <AppText variant="bodySm" tone="danger">{error}</AppText> : null}
        <Button
          title={leaveAtGate && kind === 'Delivery' ? t('log_parcel') : t('ask_resident')}
          size="lg"
          loading={busy}
          disabled={!canSubmit}
          onPress={() => void submit()}
        />
      </Screen>
    </Screen>
  );
}
